import { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { isPlatformAdmin, useOrganizationScope } from "../contexts/OrganizationScopeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Building2, Plus, MoreVertical, Pencil, Trash2, Search, Crown, Sparkles, Shield, Eye } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export const Organizations = () => {
  const { user } = useAuth();
  const {
    activeOrganizationId,
    clearScope,
    loading,
    organizations,
    organizationsError,
    refreshOrganizations,
    setActiveOrganization,
  } = useOrganizationScope();
  const { t } = useLanguage();
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [formData, setFormData] = useState({
    company_name: "",
    operation_type: "industrial",
    operation_category: "open",
    operational_authorization_number: "",
    responsible_person: "",
    operator_id: ""  // U-Space: EASA Operator ID
  });
  const [editingTier, setEditingTier] = useState(null);
  const [selectedTier, setSelectedTier] = useState("");

  const tierConfig = {
    free: { icon: Shield, label: "Free", labelCs: "Zdarma", color: "bg-slate-100 text-slate-700 border-slate-300" },
    professional: { icon: Sparkles, label: "Professional", labelCs: "Professional", color: "bg-blue-50 text-blue-700 border-blue-300" },
    enterprise: { icon: Crown, label: "Enterprise", labelCs: "Enterprise", color: "bg-purple-50 text-purple-700 border-purple-300" }
  };

  useEffect(() => {
    if (organizationsError) {
      toast.error(t.organizations?.errorLoading || t.errors?.failedToLoadOrganizations || "Failed to fetch organizations");
    }
  }, [organizationsError, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingOrg) {
        await api.put(`/organizations/${editingOrg.id}`, formData);
        toast.success(t.organizations?.updated || "Organization updated successfully");
      } else {
        await api.post('/organizations', formData);
        toast.success(t.organizations?.created || "Organization created successfully");
      }
      setShowDialog(false);
      setEditingOrg(null);
      resetForm();
      await refreshOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || t.organizations?.saveError || "Failed to save organization");
    }
  };

  const handleDelete = async (org) => {
    if (!window.confirm(`Delete ${org.company_name}?`)) return;
    try {
      await api.delete(`/organizations/${org.id}`);
      toast.success(t.organizations?.deleted || "Organization deleted");
      if (activeOrganizationId === org.id) {
        clearScope();
      }
      await refreshOrganizations();
    } catch (error) {
      toast.error(t.organizations?.deleteError || "Failed to delete organization");
    }
  };

  const handleEdit = (org) => {
    setEditingOrg(org);
    setFormData({
      company_name: org.company_name,
      operation_type: org.operation_type,
      operation_category: org.operation_category,
      operational_authorization_number: org.operational_authorization_number || "",
      responsible_person: org.responsible_person,
      operator_id: org.operator_id || ""
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      company_name: "",
      operation_type: "industrial",
      operation_category: "open",
      operational_authorization_number: "",
      responsible_person: "",
      operator_id: ""
    });
  };

  const handleUpdateTier = async (orgId, newTier) => {
    try {
      await api.put(
        `/organizations/${orgId}/subscription`,
        { subscription_tier: newTier }
      );
      toast.success((t.organizations?.subscriptionUpdated || "Subscription updated to {tier}").replace("{tier}", newTier));
      refreshOrganizations();
      setEditingTier(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t.organizations?.subscriptionUpdateError || "Failed to update subscription");
    }
  };

  const getTierBadge = (tier) => {
    const config = tierConfig[tier] || tierConfig.free;
    const TierIcon = config.icon;
    return (
      <Badge variant="outline" className={`gap-1 ${config.color}`}>
        <TierIcon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getScoreBadge = (score) => {
    if (score >= 90) return <Badge className="bg-emerald-100 text-emerald-800">{score}</Badge>;
    if (score >= 70) return <Badge className="bg-amber-100 text-amber-800">{score}</Badge>;
    return <Badge className="bg-rose-100 text-rose-800">{score}</Badge>;
  };

  const filteredOrgs = organizations.filter(org =>
    org.company_name.toLowerCase().includes(search.toLowerCase()) ||
    org.responsible_person.toLowerCase().includes(search.toLowerCase())
  );

  if (!isPlatformAdmin(user)) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <Building2 className="h-16 w-16 text-slate-300 mb-4" />
        <p className="text-lg font-medium text-slate-600">Access Restricted</p>
        <p className="text-sm text-slate-500">Only platform admins can manage organizations</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="organizations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            {t.organizations?.title || "Organizations"}
          </h1>
          <p className="text-slate-500 mt-1">{t.organizations?.subtitle || "Manage drone operator companies"}</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setEditingOrg(null); setShowDialog(true); }}
          className="gap-2 bg-primary hover:bg-primary/90"
          data-testid="add-organization-btn"
        >
          <Plus className="h-4 w-4" /> {t.organizations?.addButton || "Add Organization"}
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={t.organizations?.searchPlaceholder || "Search organizations..."}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-organizations"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.organizations?.columns?.companyName || "Company Name"}</TableHead>
                <TableHead>{t.organizations?.columns?.type || "Type"}</TableHead>
                <TableHead>{t.organizations?.columns?.category || "Category"}</TableHead>
                <TableHead>{t.organizations?.columns?.subscription || "Subscription"}</TableHead>
                <TableHead>{t.organizations?.columns?.authorization || "Authorization"}</TableHead>
                <TableHead>{t.organizations?.columns?.responsiblePerson || "Responsible Person"}</TableHead>
                <TableHead>{t.organizations?.columns?.compliance || "Compliance"}</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : organizationsError ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500 mb-3">{t.organizationScope.loadError}</p>
                    <Button variant="outline" size="sm" onClick={() => refreshOrganizations()} data-testid="organizations-retry-button">
                      {t.organizationScope.retryAction}
                    </Button>
                  </TableCell>
                </TableRow>
              ) : filteredOrgs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">{t.organizations?.emptyState || "No organizations found"}</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrgs.map((org) => (
                  <TableRow key={org.id} data-testid={`org-row-${org.id}`}>
                    <TableCell className="font-medium">{org.company_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">{org.operation_type}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">{org.operation_category}</Badge>
                    </TableCell>
                    <TableCell>
                      {editingTier === org.id ? (
                        <Select 
                          value={selectedTier} 
                          onValueChange={(value) => {
                            handleUpdateTier(org.id, value);
                          }}
                        >
                          <SelectTrigger className="w-32 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="professional">Professional</SelectItem>
                            <SelectItem value="enterprise">Enterprise</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <button 
                          onClick={() => {
                            setEditingTier(org.id);
                            setSelectedTier(org.subscription_tier || 'enterprise');
                          }}
                          className="cursor-pointer hover:opacity-80"
                        >
                          {getTierBadge(org.subscription_tier || 'enterprise')}
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {org.operational_authorization_number || "-"}
                    </TableCell>
                    <TableCell>{org.responsible_person}</TableCell>
                    <TableCell>{getScoreBadge(org.compliance_score)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`org-actions-${org.id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => setActiveOrganization(org.id)}
                            disabled={activeOrganizationId === org.id}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            {activeOrganizationId === org.id
                              ? t.organizationScope.viewingOrganization
                              : t.organizationScope.viewOrganization}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(org)}>
                            <Pencil className="h-4 w-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(org)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingOrg ? "Edit Organization" : "Add Organization"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="organization-form">
            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name *</Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                required
                data-testid="org-company-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Operation Type *</Label>
                <Select
                  value={formData.operation_type}
                  onValueChange={(value) => setFormData({ ...formData, operation_type: value })}
                >
                  <SelectTrigger data-testid="org-operation-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="industrial">Industrial</SelectItem>
                    <SelectItem value="agriculture">Agriculture</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Operation Category *</Label>
                <Select
                  value={formData.operation_category}
                  onValueChange={(value) => setFormData({ ...formData, operation_category: value })}
                >
                  <SelectTrigger data-testid="org-operation-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="specific">Specific</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="auth_number">Authorization Number</Label>
              <Input
                id="auth_number"
                value={formData.operational_authorization_number}
                onChange={(e) => setFormData({ ...formData, operational_authorization_number: e.target.value })}
                placeholder="EASA-XX-XXXX-XXXX"
                data-testid="org-auth-number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="responsible">Responsible Person *</Label>
              <Input
                id="responsible"
                value={formData.responsible_person}
                onChange={(e) => setFormData({ ...formData, responsible_person: e.target.value })}
                required
                data-testid="org-responsible-person"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="operator_id">{t.uSpace?.operatorId || "EASA Operator ID"}</Label>
              <Input
                id="operator_id"
                value={formData.operator_id}
                onChange={(e) => setFormData({ ...formData, operator_id: e.target.value })}
                placeholder="CZE-OP-XXXXXX"
                data-testid="org-operator-id"
              />
              <p className="text-xs text-muted-foreground">
                {t.uSpace?.operatorIdHelper || "Operator registration number from EASA/CAA registry"}
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="org-submit-btn">
                {editingOrg ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
